import { useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon, MessageCircleIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { MentionedProducts } from '../journal/MentionedProducts';
import { ProductImage } from '../../ui/ProductImage';
import { articlePath } from '../journal/journalRules';
import { useCatalog } from '../../contexts/CatalogContext';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { useLayerY } from '../../hooks/useParallax';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { HOME_ARTICLES, JOURNAL_PRODUCTS } from './homeRules';

/** Hoe snel het zwevende beeld de muis volgt. */
const FOLLOW = {
  stiffness: 380,
  damping: 32,
  mass: 0.6
};

/**
 * "Vragen die ik in de salon het vaakst krijg", op klei: het enige blok dat Sarahs expertise
 * laat zien krijgt een eigen, donker vlak (shell-tekst, ≥ 5,6:1; nooit muted of kleine blush op klei).
 *
 * - Links het nieuwste artikel groot; daaronder "Uit dit artikel": de genoemde producten op een
 *   licht vlak, met een plusje. Zo verkoopt het journal ook.
 * - Rechts de andere vragen als rijen. Wijs je er met de muis een aan, dan zweeft het beeld mee
 *   (alleen computer met muis, niet bij "minder beweging").
 * - Onderaan de app-kaart in blush: het lichtste vlak, dus het slot van het blok.
 */
export function HomeJournal() {
  const pages = usePages();
  const {
    articles,
    productsByIds
  } = useCatalog();
  const reduce = useReducedMotion();
  const copy = pages.home.journal;
  const [lead, ...rest] = articles.slice(0, HOME_ARTICLES);
  const [hovered, setHovered] = useState<string | null>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, FOLLOW);
  const y = useSpring(my, FOLLOW);
  // De grote foto beweegt binnen zijn kader licht mee.
  const leadRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: leadRef,
    offset: ['start end', 'end start']
  });
  const leadY = useLayerY(scrollYProgress, [-28, 28]);
  if (!lead) return null;
  const mentioned = productsByIds(lead.productIds ?? []).slice(0, JOURNAL_PRODUCTS);
  // Alleen een voorbeeld met een echt beeld; `previewImage` is dan altijd een niet-lege tekst.
  const preview = rest.find(a => a.slug === hovered && a.image);
  const previewImage = preview?.image || null;
  const onMove = (e: MouseEvent<HTMLUListElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - box.left);
    my.set(e.clientY - box.top);
  };
  return <section className="bg-clay py-20 text-shell sm:py-24 lg:py-32" aria-labelledby="home-journal" data-id="element-7378" data-mp="1apch0lnhsq6u">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7379" data-mp="1j5bjuuajw2z6">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7380" data-mp="1n8zszhetz6mj">
          <div className="max-w-2xl" data-id="element-7381" data-mp="198oz05i85bx5">
            <h2 id="home-journal" className={sectionHeading} data-id="element-7382" data-mp="199qnbbo93uwt">
              <HeadingText heading={copy.heading} accentClassName="text-blush" data-id="element-7383" data-mp="1e7s6dwlofu8a" />
            </h2>
            {copy.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed" data-id="element-7384" data-mp="21xmclvbhqkjk">{fill(copy.body)}</p>}
          </div>
          {copy.cta && <Link to="/journal" className={`${quietLink} text-shell`} data-id="element-7385" data-mp="26v5ud57edfg9">
              {fill(copy.cta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7386" data-mp="1s6rtgztjk12c" />
            </Link>}
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7387" data-mp="1x4b65valc5s6">
          <article className="lg:col-span-7" data-id="element-7388" data-mp="2kunntxvn1gvy">
            <Link to={articlePath(lead)} className="group block" data-id="element-7389" data-mp="2pmov15dy8hf4">
              <div ref={leadRef} className="relative aspect-[4/3] overflow-hidden bg-ink/20" data-id="element-7390" data-mp="2po9t34zh2ihq">
                <motion.div style={leadY ? {
                y: leadY
              } : undefined} className="absolute inset-x-0 -inset-y-[5%]" data-id="element-7391" data-mp="112guthlkzkbe">
                  <MediaView media={{
                  image: lead.image || founder.portraitPalm,
                  alt: ''
                }} sizes="(min-width: 1024px) 55vw, 100vw" className="h-full w-full transition-transform duration-300 ease-soft group-hover:scale-[1.03]" data-id="element-7392" data-mp="x1wwiap9uano" />
                </motion.div>
              </div>
              <p className="mt-6 text-[13px]" data-id="element-7393" data-mp="1fr37tcnqjzzg">
                {lead.category} · {lead.readTime}
              </p>
              <h3 className="mt-2 font-display text-[2rem] font-light leading-[1.1] underline decoration-transparent underline-offset-[8px] transition-[text-decoration-color] duration-150 ease-soft group-hover:decoration-shell/60 sm:text-[2.4rem]" data-id="element-7394" data-mp="1axquvzsf5hmj">
                {lead.title}
              </h3>
              <p className="mt-3 max-w-xl text-[16px] leading-relaxed" data-id="element-7395" data-mp="1ynbfso84uoyg">{lead.excerpt}</p>
            </Link>

            {mentioned.length > 0 && <motion.div initial={reduce ? false : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '0px 0px -10% 0px'
          }} transition={{
            duration: 0.25,
            ease
          }} className="mt-8 bg-shell p-5 text-ink sm:p-6" data-id="element-7396" data-mp="2ayvp3kbdq9g2">
                <h4 className="text-[13px] text-muted" data-id="element-7397" data-mp="2oy562m7et7l3">Uit dit artikel</h4>
                <MentionedProducts products={mentioned} data-id="element-7398" data-mp="2tx807bluptea" />
              </motion.div>}
          </article>

          {rest.length > 0 && <ul onMouseMove={reduce ? undefined : onMove} onMouseLeave={() => setHovered(null)} className="relative divide-y divide-shell/25 border-y border-shell/25 lg:col-span-5 lg:self-start" data-id="element-7399" data-mp="2yqszd6z6ktf2">
              {rest.map(a => <li key={a.slug} data-id="element-7400" data-mp="3cor21f4531eq">
                  <Link to={articlePath(a)} onMouseEnter={() => setHovered(a.slug)} className="group grid grid-cols-[1fr_auto] gap-4 py-7" data-id="element-7401" data-mp="1djr1vj5rctaz">
                    <span data-id="element-7402" data-mp="18lxu4lot3r8l">
                      <span className="block text-[13px]" data-id="element-7403" data-mp="18mh9pvqzi3jg">
                        {a.category} · {a.readTime}
                      </span>
                      <span className="mt-2 block font-display text-[1.5rem] font-light leading-snug transition-transform duration-200 ease-soft group-hover:translate-x-1" data-id="element-7404" data-mp="1hkt0bxrup1bw">
                        {a.title}
                      </span>
                      <span className="mt-2 block text-[15px] leading-relaxed" data-id="element-7405" data-mp="1rackd6rav18c">{a.excerpt}</span>
                    </span>
                    <ArrowUpRightIcon className="mt-1 h-5 w-5 transition-transform duration-200 ease-soft group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7406" data-mp="258t1lrmsga4q" />
                  </Link>
                </li>)}

              {!reduce && <AnimatePresence data-id="element-7407" data-mp="3sx9wmzgs2sa2">
                  {preview && previewImage && <motion.div key={preview.slug} aria-hidden="true" style={{
              x,
              y
            }} initial={{
              opacity: 0,
              scale: 0.96,
              rotate: -2
            }} animate={{
              opacity: 1,
              scale: 1,
              rotate: -4
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.18,
              ease
            }} className="pointer-events-none absolute left-0 top-0 z-raised hidden lg:block" data-id="element-7408" data-mp="rwb7l9hcsq9">
                      <div className="-translate-x-[115%] -translate-y-1/2 bg-shell p-2 shadow-[0_18px_40px_-16px_rgba(43,36,32,0.45)]" data-id="element-7409" data-mp="30a5nqjekdtt0">
                        <ProductImage src={previewImage} alt="" fit="cover" width={176} className="h-[13rem] w-44" data-id="element-7410" data-mp="yg6d8099nghd" />
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </ul>}
        </div>

        {/* De app-kaart: het lichtste vlak, dus het slot van het blok. */}
        {copy.askLine && <div className="mt-14 flex flex-col gap-5 bg-blush p-6 text-ink sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:p-8 lg:mt-16" data-id="element-7411" data-mp="22a6lvhc3tn17">
            <div className="flex items-start gap-4" data-id="element-7412" data-mp="1y8bm4h9tnhpj">
              <MessageCircleIcon className="mt-1.5 h-5 w-5 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7413" data-mp="1t9gqgeqr9yo0" />
              <div data-id="element-7414" data-mp="1oadwpqg87evc">
                <p className="font-display text-[1.5rem] font-light leading-snug sm:text-[1.75rem]" data-id="element-7415" data-mp="1o9vqq23rhma8">{fill(copy.askLine)}</p>
                {copy.askBody && <p className="mt-1 text-[15px] leading-relaxed text-muted" data-id="element-7416" data-mp="1f9z03uxtadcj">{fill(copy.askBody)}</p>}
              </div>
            </div>
            {copy.askCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-full bg-ink px-6 text-[15px] text-shell transition-[background-color,transform] duration-150 ease-soft hover:bg-clay active:scale-[0.98]" data-id="element-7417" data-mp="3dosjw6blitoq">
                {fill(copy.askCta)}
                <span className="sr-only" data-id="element-7418" data-mp="158533xcoay69">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>}
      </div>
    </section>;
}